// Helpers et primitives de design partagés entre tous les graphes Recharts -
// dashboard, KPI direction, KPIs futures. Centralisés pour garantir la
// consistance visuelle (style Stripe : axes invisibles, grille horizontale
// quasi-blanche, tooltip sur fond surélevé, palette stable).

// Couleur du texte des ticks d'axe = `text-text-muted` (#6B7280) pour
// matcher les CardDescription. `tickLine` et `axisLine` désactivés pour
// retirer les lignes parasites.
export const AXIS_STYLE = {
  stroke: '#6B7280',
  fontSize: 11,
  tickLine: false,
  axisLine: false,
} as const;

// Grille horizontale très claire uniquement (verticale supprimée) - repère
// visuel sans "bloc" graphique.
export const GRID_STYLE = {
  stroke: '#F3F4F6',
  strokeDasharray: '0',
  vertical: false,
} as const;

// Palette stable utilisée pour les segments de pie/donut et les séries
// catégorielles. Ordre : bleu accent, vert, orange Stripe, rouge, bleu clair,
// violet, rose, mauve. L'orange `#F97316` a remplacé le jaune `#FBBF24`
// qui jurait dans la palette Stripe Dashboard.
export const CHART_COLORS = [
  '#3E63DD',
  '#34D399',
  '#F97316',
  '#F87171',
  '#60A5FA',
  '#C084FC',
  '#F472B6',
  '#A78BFA',
] as const;

// Formate un montant en € avec abbreviation k€/M€ - utilisé sur tous les
// axes monétaires et tooltips.
export function formatEuroCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)} k€`;
  return `${Math.round(n)} €`;
}

/**
 * Convertit un mois ISO (`'2026-01'`) en libellé court FR (`'janv.'`) en
 * passant par un Date local pour éviter le bug de timezone : `new Date(
 * '2026-01-01')` est parsé en UTC, donc à Paris (UTC+1/+2) le 1er janvier
 * minuit UTC tombe le 31 décembre 23h → toLocaleDateString rend "déc.".
 * On construit explicitement en heure locale au 15 du mois (au milieu, à
 * l'abri de tout shift).
 */
export function formatMonthLabel(isoMonth: string): string {
  const [yStr, mStr] = isoMonth.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return isoMonth;
  const d = new Date(y, m - 1, 15);
  return d.toLocaleDateString('fr-FR', { month: 'short' });
}

/**
 * Convertit un mois ISO (`'2026-04'`) en bornes UTC `[start, end]` pour
 * filtrer un range côté backend. Évite les pièges TZ : on ancre en UTC
 * pour que `toISOString()` ne décale pas en heure locale.
 */
export function monthToIsoRange(isoMonth: string): { start: string; end: string } | null {
  const [yStr, mStr] = isoMonth.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  const start = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0, 0));
  // Dernière ms du mois : Date.UTC(y, m, 0) = dernier jour du mois m-1+1
  const end = new Date(Date.UTC(y, m, 0, 23, 59, 59, 999));
  return { start: start.toISOString(), end: end.toISOString() };
}

/**
 * Label court d'un bucket temporel (backend renvoie toujours le timestamp
 * de début de bucket en ISO complet). Selon la granularité :
 *  - `month` : 'janv.' (réutilise formatMonthLabel pour rétrocompat)
 *  - `week` : '17 mars' (jour + mois du lundi début de semaine)
 *  - `day` : '17/03' (jour/mois pur)
 */
export function formatBucketLabel(iso: string, granularity: 'day' | 'week' | 'month'): string {
  if (granularity === 'month') {
    // Backend peut renvoyer soit 'YYYY-MM' soit un datetime ISO. On normalise.
    return formatMonthLabel(iso.slice(0, 7));
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (granularity === 'week') {
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  }
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/**
 * Rend uniques les labels d'une série ordonnée de buckets temporels. Quand une
 * fenêtre traverse plusieurs années (ex. « Cette année » = 02/01/N → 01/01/N+1,
 * qui produit 13 mois → deux buckets `janv.`), deux buckets peuvent avoir le
 * MÊME label court. Recharts résout alors le survol/clic vers la PREMIÈRE
 * occurrence de la catégorie → tooltip ET drill-down faux sur le dernier point.
 *
 * On suffixe l'année courte (`janv. 26` / `janv. 27`) UNIQUEMENT aux labels en
 * collision ; les buckets non ambigus restent inchangés (`févr.`, `mars`…).
 * `isoStarts` = timestamps de début de bucket (mêmes valeurs que `monthIso`).
 */
export function disambiguateBucketLabels(
  isoStarts: string[],
  granularity: 'day' | 'week' | 'month',
): string[] {
  const counts = new Map<string, number>();
  for (const iso of isoStarts) {
    const l = formatBucketLabel(iso, granularity);
    counts.set(l, (counts.get(l) ?? 0) + 1);
  }
  return isoStarts.map((iso) => {
    const label = formatBucketLabel(iso, granularity);
    if ((counts.get(label) ?? 0) <= 1) return label;
    const y = new Date(iso).getUTCFullYear();
    return Number.isNaN(y) ? label : `${label} ${String(y).slice(2)}`;
  });
}

/**
 * Bornes UTC `[start, end]` pour le drill-down sur un bucket cliqué.
 * - `month` : délègue à `monthToIsoRange`.
 * - `week` : lundi 00:00 → dimanche 23:59:59 (+7j -1ms).
 * - `day` : jour 00:00 → 23:59:59.
 */
export function bucketToIsoRange(
  iso: string,
  granularity: 'day' | 'week' | 'month',
): { start: string; end: string } | null {
  if (granularity === 'month') return monthToIsoRange(iso.slice(0, 7));
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const days = granularity === 'week' ? 7 : 1;
  const end = new Date(start.getTime() + days * 86_400_000 - 1);
  return { start: start.toISOString(), end: end.toISOString() };
}

interface ChartTooltipPayloadEntry {
  name?: string;
  value?: number;
  color?: string;
  dataKey?: string;
}

/**
 * Tooltip custom façon Stripe - carte blanche avec ombre subtile et puces
 * colorées par série. Format de la valeur paramétrable (€, %, nombre…).
 */
export function ChartTooltip({
  active,
  payload,
  label,
  formatValue,
}: {
  active?: boolean;
  payload?: ChartTooltipPayloadEntry[];
  label?: string;
  formatValue?: (v: number) => string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-border-subtle bg-white px-3 py-2 shadow-popover">
      {label && <div className="mb-1 text-[11px] font-medium text-text-muted">{label}</div>}
      <div className="space-y-1">
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: p.color }}
            />
            <span className="text-text-muted">{p.name}</span>
            <span className="ml-auto font-semibold text-brand">
              {formatValue && typeof p.value === 'number'
                ? formatValue(p.value)
                : (p.value?.toLocaleString('fr-FR') ?? '-')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
